import React from 'react';
import { ActionIcon, Switch } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import CollapsibleListField from '../../../puck/fields/CollapsibleListField';
import { PuckColorFieldWithPalette } from '../../../puck/fields/createPuckColorField';
import ButtonLinkField from '../../../puck/fields/ButtonLinkField';
import ButtonPreview from '../../../puck/fields/ButtonPreview';
import ButtonStylePicker from '../../../puck/fields/ButtonStylePicker';
import ButtonStyleField from '../../../puck/fields/ButtonStyleField';
import { BorderControls } from '../../../puck/fields/puckBorderField';
import { slideButtonProps } from './carouselDefaults';
import { resolveButtonBorder } from '../../../../shared/boxes/button/buttonStyles';
import TextBackgroundColorField from '../../../puck/fields/TextBackgroundColorField';
import MediaSourceField from '../../../puck/fields/MediaSourceField';
import { PxWidthInput } from '../../../puck/fields/puckPxWidthField';
import { isMediaPresent } from '../../../media';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { notifySlidePreview } from '../../../utils/carouselSlidesRegistry';
import { carouselSlideDefaults } from './carouselDefaults';

/** Bleu Mantine (blue-6) — couleur effective quand `ctaColor` est vide. */
const DEFAULT_CTA_COLOR = '#228be6';

/**
 * Champ « Slides » — liste repliable partagée (le champ `array` natif de Puck
 * n'ouvre pas l'item qu'on vient d'ajouter). Titre et sous-titre s'éditent sur
 * le canvas : ne restent ici que les attributs atomiques.
 *
 * L'œil de l'en-tête amène le canvas sur la slide : replier un item n'aide pas
 * à régler une slide qu'on ne voit pas.
 */
export default function CarouselSlidesField({ value, onChange }) {
  const { itemProps } = useSelectedItemProps();
  const blockId = itemProps.id;

  return (
    <CollapsibleListField
      value={value}
      onChange={onChange}
      addLabel="Ajouter une slide"
      createItem={(count) => carouselSlideDefaults({ title: `Slide ${count + 1}` })}
      itemTitle={(item, index) => String(item.title || '').replace(/<[^>]+>/g, '').trim()
        || `Slide ${index + 1}`}
      renderItemActions={(item, index) => (
        <ActionIcon
          variant="subtle"
          color="editor"
          size="sm"
          aria-label={`Voir la slide ${index + 1} sur la page`}
          title="Voir cette slide sur la page"
          onClick={(e) => {
            e.stopPropagation();
            if (blockId) notifySlidePreview(blockId, index);
          }}
        >
          <FontAwesomeIcon icon={icon({ name: 'eye', style: 'light' })} style={{ fontSize: 14 }} />
        </ActionIcon>
      )}
      renderItem={(item, update) => (
        <>
          <MediaSourceField
            value={item.backgroundMedia}
            onChange={(backgroundMedia) => update({ backgroundMedia })}
            fieldLabel="Média de fond"
            mediaFit={itemProps.mediaFit}
          />
          <MediaSourceField
            value={item.contentMedia}
            onChange={(contentMedia) => update({ contentMedia })}
            fieldLabel="Média du contenu"
            mediaFit="contain"
          />
          {/* Par slide, et sur le média du contenu seulement : le média de fond
              remplit le cadre, il n'a pas de largeur propre. */}
          {isMediaPresent(item.contentMedia) && (
            <PxWidthInput
              value={item.contentMediaWidth}
              onChange={(contentMediaWidth) => update({ contentMediaWidth })}
              label="Largeur de l'image"
            />
          )}
          <TextBackgroundColorField
            label="Couleur de fond de la slide"
            value={item.slideBackground || ''}
            onChange={(slideBackground) => update({ slideBackground })}
          />
          {/* Le libellé du bouton s'édite sur le canvas (comme le titre) : ne
              restent ici que le lien et la couleur. */}
          <Switch
            size="sm"
            label="Bouton"
            description="Le texte du bouton s'écrit directement sur la page."
            checked={!!item.ctaEnabled}
            onChange={(e) => update({ ctaEnabled: e.currentTarget.checked })}
          />
          {item.ctaEnabled && (
            <>
              {/* Un bouton est un bouton : la slide reçoit les mêmes réglages
                  que la box Bouton. Ils s'écrivent dans l'item de la liste,
                  d'où les props explicites — le bloc sélectionné est le
                  Carrousel, pas la slide. */}
              <ButtonLinkField
                value={item.ctaLink}
                onChange={(ctaLink) => update({ ctaLink })}
                legacyUrl={item.ctaUrl}
              />
              <ButtonPreview
                buttonProps={slideButtonProps(item)}
                label={item.ctaLabel}
              />
              <ButtonStylePicker
                buttonProps={slideButtonProps(item)}
                onPatch={(patch) => update({
                  ctaButtonStyle: patch.buttonStyle,
                  ctaBorder: patch.border,
                })}
              />
              <PuckColorFieldWithPalette
                label="Couleur du bouton"
                value={item.ctaColor || DEFAULT_CTA_COLOR}
                onChange={(ctaColor) => update({ ctaColor: ctaColor ?? '' })}
              />
              <BorderControls
                value={item.ctaBorder}
                onChange={(ctaBorder) => update({ ctaBorder })}
                defaults={resolveButtonBorder()}
              />
              <ButtonStyleField
                value={item.ctaButtonStyle}
                onChange={(ctaButtonStyle) => update({ ctaButtonStyle })}
              />
            </>
          )}
        </>
      )}
    />
  );
}
